import React, { useState, useMemo, useEffect, useRef } from "react";
import { useVerbatim } from "../context/VerbatimContext";
import { useSetup } from "../context/SetupContext";
import LabelChip from "../components/LabelChip";
import "./MergeLabelTypesModal.css";

const PROTECTED_TYPES = ["Gender", "Age", "Sentiment", "Sources"];
const SENTIMENT_STATES = ["positive", "negative", "mixed"];
const SENTIMENT_CYCLE = ["inactive", "positive", "negative", "mixed"];
const TYPE_A_COLOR = "#F59E0B";
const TYPE_B_COLOR = "#5034C6";
const SELECTED_COLOR = "#279E9B";
const OPTION_KEYS = ["mandatory", "sentimentEnabled", "multiValue"];
const OPTION_LABELS = {
	mandatory: "Mandatory",
	sentimentEnabled: "With sentiment",
	multiValue: "Multiple values",
};

const OPTION_TOOLTIPS = {
	mandatory: "Makes the new type mandatory for all impacted verbatims. If left unchecked, it remains optional.",
	sentimentEnabled: "This option keeps sentiment logic on the new type. If the merged labels carry the same sentiment, it is preserved. When sentiments differ, the following rules apply: negative + positive = mixed, mixed + positive = positive, negative + mixed = negative. If one tag does not carry a sentiment, it becomes positive by default. If you uncheck this option, all sentiments from the merged labels are removed.",
	multiValue: "This option is checked by default to merge all labels in each verbatim. If you uncheck it, you will need to choose the labels manually for each verbatim. For example, if the merge involves 1000 verbatims, you will have to handle the labels one by one for each of them.",
};

// Merge two sentiment states (same logic as MergeLabelsModal)
const mergeSentiments = (sentimentA, sentimentB) => {
	const normA = sentimentA || "active";
	const normB = sentimentB || "active";
	if (normA === normB) return normA;
	if (normA === "mixed" || normB === "mixed") {
		if ((normA === "mixed" && normB === "positive") || (normA === "positive" && normB === "mixed")) return "positive";
		if ((normA === "mixed" && normB === "negative") || (normA === "negative" && normB === "mixed")) return "negative";
	}
	if ((normA === "positive" && normB === "negative") || (normA === "negative" && normB === "positive")) return "mixed";
	return "mixed";
};

// Normalize a tag state depending on source/target sentimentEnabled
const normalizeState = (state, sourceHasSentiment, newSentimentEnabled) => {
	if (!newSentimentEnabled) return "active";
	if (sourceHasSentiment && SENTIMENT_STATES.includes(state)) return state;
	return "positive";
};

// Compute the pre-fill tagging map from verbatims
const computeTaggingMap = (verbatims, typeA, typeB, selectedLabels, newTypeOptions, sentimentEnabledA, sentimentEnabledB, newTypeName) => {
	const { multiValue, sentimentEnabled: newSentimentEnabled } = newTypeOptions;
	const selectedNames = new Set(selectedLabels.map((l) => l.name));
	const issues = {};
	const map = {};

	verbatims.forEach((verbatim) => {
		const sourceTagsA = (verbatim.labels?.[typeA] || []).filter(Boolean);
		const sourceTagsB = (verbatim.labels?.[typeB] || []).filter(Boolean);
		const hasAnySourceTag = sourceTagsA.length > 0 || sourceTagsB.length > 0;
		const hasUnselectedSourceTag = [...sourceTagsA, ...sourceTagsB].some((t) => !selectedNames.has(t.name));

		if (!hasAnySourceTag) {
			map[verbatim.id] = null; // Not impacted
			return;
		}

		if (hasUnselectedSourceTag) {
			map[verbatim.id] = [];
			issues[verbatim.id] = "unselected";
			return;
		}

		const tagsA = sourceTagsA
			.filter((t) => selectedNames.has(t.name))
			.map((t) => ({ ...t, type: newTypeName, state: normalizeState(t.state, sentimentEnabledA, newSentimentEnabled) }));

		const tagsB = sourceTagsB
			.filter((t) => selectedNames.has(t.name))
			.map((t) => ({ ...t, type: newTypeName, state: normalizeState(t.state, sentimentEnabledB, newSentimentEnabled) }));

		// Merge by name : same label in A and B → merge sentiment states
		const tagsByName = {};
		tagsA.forEach((t) => { tagsByName[t.name] = t; });
		tagsB.forEach((t) => {
			if (tagsByName[t.name]) {
				const mergedState = newSentimentEnabled
					? mergeSentiments(tagsByName[t.name].state, t.state)
					: "active";
				tagsByName[t.name] = { ...tagsByName[t.name], state: mergedState };
			} else {
				tagsByName[t.name] = t;
			}
		});

		const dedupedTags = Object.values(tagsByName);

		if (dedupedTags.length === 0) {
			map[verbatim.id] = [];
			issues[verbatim.id] = "manual";
			return;
		}

		if (!multiValue && dedupedTags.length > 1) {
			// Conflict : multiValue=false but two different labels
			map[verbatim.id] = [];
			issues[verbatim.id] = "conflict";
		} else {
			// Force type: newTypeName on each tag
			map[verbatim.id] = multiValue
				? dedupedTags.map((t) => ({ ...t, type: newTypeName }))
				: [{ ...dedupedTags[0], type: newTypeName }];
		}
	});

	return { map, issues };
};

// Merge label definitions (keywords + explanation) from A and B
const mergeDefinitions = (labelName, labelsA, labelsB, newTypeName) => {
	const defA = labelsA?.find((l) => l.name === labelName);
	const defB = labelsB?.find((l) => l.name === labelName);
	const kwA = defA?.keywords || [];
	const kwB = defB?.keywords || [];
	const expA = defA?.explanation || "";
	const expB = defB?.explanation || "";
	return {
		name: labelName,
		keywords: [...new Set([...kwA, ...kwB])],
		explanation: expA && expB ? `${expA}\n${expB}` : expA || expB,
		mandatory: false,
		sentimentEnabled: false,
		type: newTypeName,
	};
};

const MergeLabelTypesModal = ({ isOpen, onClose }) => {
	const { verbatims } = useVerbatim();
	const { labels, labelTypes, mergeLabelTypes } = useSetup();

	// Step 1 state
	const [step, setStep] = useState(1);
	const [typeA, setTypeA] = useState("");
	const [typeB, setTypeB] = useState("");
	const [newTypeName, setNewTypeName] = useState("");
	const [newTypeOptions, setNewTypeOptions] = useState({ mandatory: false, sentimentEnabled: false, multiValue: true });
	const [selectedLabelNames, setSelectedLabelNames] = useState([]);
	const [step1Error, setStep1Error] = useState("");
	const normalizedNewTypeName = newTypeName.trim();
	const [selectDeselectAll, setSelectDeselectAll] = useState("");

	// Step 2 state
	const [taggingMap, setTaggingMap] = useState({});
	const [taggingIssues, setTaggingIssues] = useState({});
	const [step2Error, setStep2Error] = useState("");
	const rowRefs = useRef({});

	// Reset on open/close
	useEffect(() => {
		if (!isOpen) {
			setStep(1);
			setTypeA("");
			setTypeB("");
			setNewTypeName("");
			setNewTypeOptions({ mandatory: false, sentimentEnabled: false, multiValue: true });
			setSelectedLabelNames([]);
			setStep1Error("");
			setTaggingMap({});
			setTaggingIssues({});
			setStep2Error("");
			setSelectDeselectAll("");
		}
	}, [isOpen]);

	// Available types for dropdowns
	const availableTypes = useMemo(() =>
		Object.keys(labelTypes).filter((t) => !PROTECTED_TYPES.includes(t)),
		[labelTypes]
	);

	// Deduplicated label list from A + B (by name)
	const combinedLabels = useMemo(() => {
		if (!typeA || !typeB) return [];
		const labelsA = labels[typeA] || [];
		const labelsB = labels[typeB] || [];
		const labelsByName = new Map();

		const upsertLabel = (label, sourceKey) => {
			if (!labelsByName.has(label.name)) {
				labelsByName.set(label.name, {
					name: label.name,
					inTypeA: false,
					inTypeB: false,
				});
			}
			labelsByName.get(label.name)[sourceKey] = true;
		};

		labelsA.forEach((label) => upsertLabel(label, "inTypeA"));
		labelsB.forEach((label) => upsertLabel(label, "inTypeB"));

		return [...labelsByName.values()];
	}, [typeA, typeB, labels]);

	const mergedTypeOptions = useMemo(() => {
		if (!typeA || !typeB) return null;

		return {
			mandatory: !!labelTypes[typeA]?.mandatory && !!labelTypes[typeB]?.mandatory,
			sentimentEnabled: !!labelTypes[typeA]?.sentimentEnabled && !!labelTypes[typeB]?.sentimentEnabled,
			multiValue: !!labelTypes[typeA]?.multiValue && !!labelTypes[typeB]?.multiValue,
		};
	}, [typeA, typeB, labelTypes]);

	useEffect(() => {
		if (!isOpen || !mergedTypeOptions) return;
		setNewTypeOptions({
			mandatory: mergedTypeOptions.mandatory,
			sentimentEnabled: mergedTypeOptions.sentimentEnabled,
			multiValue: true,
		});
	}, [isOpen, mergedTypeOptions]);

	// Selected label definitions (merged from A and B)
	const selectedLabelDefs = useMemo(() =>
		selectedLabelNames.map((name) =>
			mergeDefinitions(name, labels[typeA], labels[typeB], normalizedNewTypeName)
		),
		[selectedLabelNames, typeA, typeB, labels, normalizedNewTypeName]
	);

	// Verbatims impacted (have at least one tag in typeA or typeB)
	const impactedVerbatims = useMemo(() => {
		if (!typeA || !typeB) return [];
		return verbatims.filter((v) => {
			const hasA = (v.labels?.[typeA] || []).length > 0;
			const hasB = (v.labels?.[typeB] || []).length > 0;
			return hasA || hasB;
		});
	}, [verbatims, typeA, typeB]);

	const remainingIssueIds = useMemo(() =>
		impactedVerbatims
			.filter((verbatim) => !Array.isArray(taggingMap[verbatim.id]) || taggingMap[verbatim.id].length === 0)
			.map((verbatim) => verbatim.id),
		[impactedVerbatims, taggingMap]
	);

	// --- Step navigation ---

	const handleGoToStep2 = () => {
		setStep1Error("");
		setStep2Error("");
		if (!typeA || !typeB) { setStep1Error("Please select both source types."); return; }
		if (typeA === typeB) { setStep1Error("Both source types must be different."); return; }
		if (!normalizedNewTypeName) { setStep1Error("New type name is required."); return; }
		if (labelTypes[normalizedNewTypeName] && normalizedNewTypeName !== typeA && normalizedNewTypeName !== typeB) {
			setStep1Error(`The name "${normalizedNewTypeName}" already exists in label types.`); return;
		}
		if (selectedLabelNames.length === 0) { setStep1Error("Select at least one label."); return; }

		// Compute tagging map
		const { map, issues: foundIssues } = computeTaggingMap(
			verbatims, typeA, typeB, selectedLabelDefs,
			newTypeOptions,
			labelTypes[typeA]?.sentimentEnabled || false,
			labelTypes[typeB]?.sentimentEnabled || false,
			normalizedNewTypeName
		);
		setTaggingMap(map);
		setTaggingIssues(foundIssues);
		setStep(2);
	};

	const handleGoToStep3 = () => {
		if (remainingIssueIds.length > 0) {
			setStep2Error(`Complete ${remainingIssueIds.length} verbatim(s) before continuing.`);
			return;
		}
		setStep2Error("");
		setStep(3);
	};

	const handleConfirmMerge = () => {
		if (remainingIssueIds.length > 0) {
			setStep2Error(`Complete ${remainingIssueIds.length} verbatim(s) before merging.`);
			setStep(2);
			return;
		}

		const confirmed = window.confirm(
			`This action is irreversible.\n\nMerge "${typeA}" and "${typeB}" into "${normalizedNewTypeName}" ?\n\nSave your project before continuing.`
		);
		if (!confirmed) return;

		// Build final tagging map (only non-null entries)
		const finalMap = {};
		Object.entries(taggingMap).forEach(([id, tags]) => {
			if (Array.isArray(tags) && tags.length > 0) {
				finalMap[id] = tags;
			}
		});

		mergeLabelTypes(typeA, typeB, normalizedNewTypeName, newTypeOptions, selectedLabelDefs, finalMap);
		onClose();
	};

	// --- Step 2 helpers ---

	const buildManualTag = (labelName) => {
		// Auto-prefill already preserves source states. Manual re-selection must restart the normal sentiment cycle.
		const state = newTypeOptions.sentimentEnabled ? "positive" : "active";

		return { name: labelName, state, type: normalizedNewTypeName };
	};

	const getNextSentimentState = (currentState) => {
		const currentIndex = SENTIMENT_CYCLE.indexOf(currentState || "inactive");
		const nextIndex = ((currentIndex === -1 ? 0 : currentIndex) + 1) % SENTIMENT_CYCLE.length;
		return SENTIMENT_CYCLE[nextIndex];
	};

	const toggleTagInMap = (verbatimId, labelName) => {
		setTaggingMap((prev) => {
			const current = prev[verbatimId] || [];
			const currentTag = current.find((t) => t.name === labelName);
			const exists = !!currentTag;
			let updated;

			if (newTypeOptions.sentimentEnabled) {
				if (!exists) {
					updated = newTypeOptions.multiValue
						? [...current, buildManualTag(labelName)]
						: [buildManualTag(labelName)];
				} else {
					const nextState = getNextSentimentState(currentTag.state);
					if (nextState === "inactive") {
						updated = current.filter((t) => t.name !== labelName);
					} else {
						updated = current.map((t) =>
							t.name === labelName ? { ...t, state: nextState } : t
						);
					}
				}
			} else if (newTypeOptions.multiValue) {
				updated = exists
					? current.filter((t) => t.name !== labelName)
					: [...current, buildManualTag(labelName)];
			} else {
				updated = exists ? [] : [buildManualTag(labelName)];
			}
			return { ...prev, [verbatimId]: updated };
		});
		setStep2Error("");
	};

	const setDropdownTag = (verbatimId, labelName) => {
		setTaggingMap((prev) => {
			const updated = labelName
				? [buildManualTag(labelName)]
				: [];
			return { ...prev, [verbatimId]: updated };
		});
		setStep2Error("");
	};

	const setDropdownSentiment = (verbatimId, sentimentState) => {
		setTaggingMap((prev) => {
			const current = prev[verbatimId] || [];
			if (!sentimentState) {
				return { ...prev, [verbatimId]: [] };
			}
			const updated = current.map((tag, index) =>
				index === 0 ? { ...tag, state: sentimentState } : tag
			);
			return { ...prev, [verbatimId]: updated };
		});
		setStep2Error("");
	};

	const scrollToRow = (id) => {
		rowRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "center" });
	};

	const remainingIssueSummary = useMemo(() => {
		const conflictCount = remainingIssueIds.filter((id) => taggingIssues[id] === "conflict").length;
		const unselectedCount = remainingIssueIds.filter((id) => taggingIssues[id] === "unselected").length;
		const manualCount = remainingIssueIds.length - conflictCount - unselectedCount;
		return { conflictCount, unselectedCount, manualCount };
	}, [remainingIssueIds, taggingIssues]);

	const getLabelChipClassName = (option, selected) => {
		const classes = ["mlt-chip-toggle"];
		if (option.inTypeA && option.inTypeB) {
			classes.push("mlt-chip-both");
		} else if (option.inTypeA) {
			classes.push("mlt-chip-type-a");
		} else if (option.inTypeB) {
			classes.push("mlt-chip-type-b");
		}
		if (selected) {
			classes.push("mlt-chip-selected");
		}
		return classes.join(" ");
	};

	const getLabelChipStyle = (option, selected) => {
		if (selected) {
			return {
				backgroundColor: SELECTED_COLOR,
				backgroundImage: "none",
			};
		}
		if (option.inTypeA && option.inTypeB) {
			return {
				backgroundImage: `linear-gradient(135deg, ${TYPE_A_COLOR} 0 49%, ${TYPE_B_COLOR} 51% 100%)`,
			};
		}
		if (option.inTypeA) {
			return { backgroundColor: TYPE_A_COLOR };
		}
		if (option.inTypeB) {
			return { backgroundColor: TYPE_B_COLOR };
		}
		return {};
	};

	const getStep2ChipClassName = (tag) => {
		const state = tag?.state || "inactive";
		if (!tag) return "mlt-chip-toggle mlt-chip-inactive";
		if (!newTypeOptions.sentimentEnabled) return "mlt-chip-toggle mlt-chip-selected";
		if (state === "positive") return "mlt-chip-toggle mlt-chip-positive";
		if (state === "negative") return "mlt-chip-toggle mlt-chip-negative";
		if (state === "mixed") return "mlt-chip-toggle mlt-chip-mixed";
		if (state === "active") return "mlt-chip-toggle mlt-chip-selected";
		return "mlt-chip-toggle mlt-chip-inactive";
	};

	const getStep2ChipLabel = (tag, labelName) => {
		if (!tag) return labelName;
		if (!newTypeOptions.sentimentEnabled) return labelName;
		return labelName;
	};

	const renderTypeSummary = (typeName, color) => {
		const typeOptions = labelTypes[typeName] || {};
		const summaryItems = [
			`Mandatory: ${typeOptions.mandatory ? "Yes" : "No"}`,
			`SentimentEnabled: ${typeOptions.sentimentEnabled ? "Yes" : "No"}`,
			`MultiValue: ${typeOptions.multiValue ? "Yes" : "No"}`,
		];

		return (
			<div className="mlt-type-summary">
				{summaryItems.map((item, index) => (
					<React.Fragment key={item}>
						<span>{item}</span>
						{index < summaryItems.length - 1 && <span className="mlt-type-summary-separator">.</span>}
					</React.Fragment>
				))}
				<span className="mlt-type-summary-separator">.</span>
				<span className="mlt-type-summary-color">
					Color: <span className="mlt-color-swatch" style={{ backgroundColor: color }} aria-hidden="true" />
				</span>
			</div>
		);
	};

	// --- Render helpers ---

	const renderTagsCell = (verbatimId) => {
		const isConflict = remainingIssueIds.includes(verbatimId);
		const issueReason = taggingIssues[verbatimId];
		const cellClass = `mlt-cell-editable${isConflict ? " mlt-conflict" : ""}`;

		if (newTypeOptions.multiValue) {
			const selectedTags = taggingMap[verbatimId] || [];
			const orderedLabelDefs = [
				...selectedLabelDefs.filter((label) => selectedTags.some((tag) => tag.name === label.name)),
				...selectedLabelDefs.filter((label) => !selectedTags.some((tag) => tag.name === label.name)),
			];

			return (
				<td className={cellClass}>
					<div className="mlt-chips-row">
						{orderedLabelDefs.map((label) => {
							const tag = selectedTags.find((t) => t.name === label.name);
							return (
								<span
									key={label.name}
									className={getStep2ChipClassName(tag)}
									onClick={() => toggleTagInMap(verbatimId, label.name)}
								>
									{getStep2ChipLabel(tag, label.name)}
								</span>
							);
						})}
					</div>
					{isConflict && <div className="mlt-issue-note">{issueReason === "unselected" ? "Source label not selected." : "Select at least one final label."}</div>}
				</td>
			);
		}

		// multiValue: false → dropdown
		const currentVal = (taggingMap[verbatimId] || [])[0]?.name || "";
		const currentTag = (taggingMap[verbatimId] || [])[0];
		return (
			<td className={cellClass}>
				<div className="mlt-dropdown-row">
					<select
						value={currentVal}
						onChange={(e) => setDropdownTag(verbatimId, e.target.value)}
					>
						<option value="">— none —</option>
						{selectedLabelNames.map((name) => (
							<option key={name} value={name}>{name}</option>
						))}
					</select>
					{currentVal && newTypeOptions.sentimentEnabled && currentTag && (
						<select
							className="mlt-sentiment-select"
							value={currentTag.state || ""}
							onChange={(e) => setDropdownSentiment(verbatimId, e.target.value)}
						>
							<option value="">No sentiment</option>
							<option value="positive">Positive</option>
							<option value="negative">Negative</option>
							<option value="mixed">Mixed</option>
						</select>
					)}
				</div>
				{isConflict && <div className="mlt-issue-note">{issueReason === "unselected" ? "Source label not selected." : "Choose one final label."}</div>}
			</td>
		);
	};

	const renderVerbatimText = (verbatim) => {
		const content = verbatim.verbatim;
		if (typeof content === "string") return content;
		if (Array.isArray(content)) return content.find((b) => b.type === "text")?.content || "";
		return "";
	};

	const renderTagList = (tags) => {
		if (!tags || tags.length === 0) return <span className="mlt-empty">—</span>;
		return tags.map((t) => (
			<LabelChip key={t.name} type={t.type} name={t.name} state={t.state} />
		));
	};

	if (!isOpen) return null;

	// --- Step 1 ---
	const renderStep1 = () => (
		<div className="mlt-step">
			<div className="mlt-row-two">
				<div className="mlt-field">
					<label className="mlt-label">Type A <span className="mlt-required">*</span></label>
					<select value={typeA} onChange={(e) => { setTypeA(e.target.value); setSelectedLabelNames([]); }}>
						<option value="">— Select —</option>
						{availableTypes.filter((t) => t !== typeB).map((t) => (
							<option key={t} value={t}>{t}</option>
						))}
					</select>
					{typeA && renderTypeSummary(typeA, TYPE_A_COLOR)}
				</div>
				<div className="mlt-field">
					<label className="mlt-label">Type B <span className="mlt-required">*</span></label>
					<select value={typeB} onChange={(e) => { setTypeB(e.target.value); setSelectedLabelNames([]); }}>
						<option value="">— Select —</option>
						{availableTypes.filter((t) => t !== typeA).map((t) => (
							<option key={t} value={t}>{t}</option>
						))}
					</select>
					{typeB && renderTypeSummary(typeB, TYPE_B_COLOR)}
				</div>
				<div className="mlt-field">
					<label className="mlt-label">New type name <span className="mlt-required">*</span></label>
					<input
						type="text"
						value={newTypeName}
						onChange={(e) => setNewTypeName(e.target.value)}
						placeholder="e.g. Evaluation"
					/>
				</div>
			</div>

			<div className="mlt-options-labels-row">
				<div className="mlt-options-block">
					<label className="mlt-label">Options <span className="mlt-required">*</span></label>
					<p className="mlt-options-helper">
						Common options from the two label types are prefilled by default for Mandatory and Sentiment. Multiple values is always checked by default. Some options may create conflicts depending on the merged label types. Read the tooltips before confirming.
					</p>
					<div className="mlt-checkboxes">
						{OPTION_KEYS.map((opt) => (
							<label key={opt} className="mlt-checkbox-label">
								<input
									type="checkbox"
									checked={newTypeOptions[opt]}
									onChange={(e) => setNewTypeOptions((prev) => ({ ...prev, [opt]: e.target.checked }))}
								/>
								<span>{OPTION_LABELS[opt]}</span>
								<div className="mlt-option-icons">
									{opt !== "mandatory" && (
										<i className="fa-solid fa-triangle-exclamation mlt-option-icon mlt-option-icon-warning" aria-hidden="true" />
									)}
									<span className="mlt-option-tooltip" data-tooltip={OPTION_TOOLTIPS[opt]} tabIndex={0}>
										<i className="fa-solid fa-circle-info mlt-option-icon mlt-option-icon-info" aria-hidden="true" />
									</span>
								</div>
							</label>
						))}
					</div>
				</div>

				{typeA && typeB && typeA !== typeB && (
					<div className="mlt-label-selection">
						<p className="mlt-label" style={{ gap: "30px" }}>
							<span>Available labels</span>
							<div style={{ display: "flex", gap: "10px" }}>
								<button 
									className={`mlt-label-btn ${selectDeselectAll === "select" ? "mlt-btn-danger" : "mlt-btn-secondary"}`} 
									onClick={() => { setSelectedLabelNames(combinedLabels.map((label) => label.name)); setSelectDeselectAll("select"); }}
								>Select All</button>
								<button 
									className={`mlt-label-btn ${selectDeselectAll === "deselect" ? "mlt-btn-danger" : "mlt-btn-secondary"}`} 
									onClick={() => { setSelectedLabelNames([]); setSelectDeselectAll("deselect"); }}
								>Deselect All</button>
							</div>
						</p>
						<div className="mlt-chips-row">
							{combinedLabels.map((label) => {
								const selected = selectedLabelNames.includes(label.name);
								return (
									<span
										key={label.name}
										className={getLabelChipClassName(label, selected)}
										style={getLabelChipStyle(label, selected)}
										onClick={() => {
											setSelectedLabelNames((prev) => selected ? prev.filter((n) => n !== label.name) : [...prev, label.name]);
											if (selectDeselectAll !== "") setSelectDeselectAll("");
										}}
									>
										{label.name}
									</span>
								);
							})}
						</div>
						{combinedLabels.length === 0 && (
							<p className="mlt-empty">No labels found in these two types.</p>
						)}
					</div>
				)}
			</div>

			{step1Error && <p className="mlt-error">{step1Error}</p>}
		</div>
	);

	// --- Step 2 ---
	const renderStep2 = () => (
		<div className="mlt-step">
			{remainingIssueIds.length > 0 && (
				<div className="mlt-alert-banner">
					<p className="mlt-alert-banner-content">
						<strong>{remainingIssueIds.length} verbatim(s) must be completed before continuing.</strong>
						<div className="mlt-alert-details">
							{remainingIssueSummary.conflictCount > 0 && <span>{remainingIssueSummary.conflictCount} conflict(s)</span>}
							{remainingIssueSummary.unselectedCount > 0 && <span>{remainingIssueSummary.unselectedCount} source label(s) not selected</span>}
							{remainingIssueSummary.manualCount > 0 && <span>{remainingIssueSummary.manualCount} empty selection(s)</span>}
						</div>
					</p>
					<div className="mlt-conflict-ids">
						IDs: {remainingIssueIds.map((id) => (
							<span key={id} className="mlt-conflict-id-link" onClick={() => scrollToRow(id)}>#{id}</span>
						))}
					</div>
				</div>
			)}
			{step2Error && <p className="mlt-error">{step2Error}</p>}

			<div className="mlt-table-wrapper" style={{height: remainingIssueIds.length > 0 ? "90%" : "100%"}}>
				<table className="mlt-table">
					<thead>
						<tr>
							<th>ID</th>
							<th style={{ width: "30%" }}>Verbatim</th>
							<th style={{ width: "20%" }}>{typeA} (before)</th>
							<th style={{ width: "20%" }}>{typeB} (before)</th>
							<th style={{ width: "25%" }}>{normalizedNewTypeName} (new)</th>
						</tr>
					</thead>
					<tbody>
						{impactedVerbatims.map((verbatim) => {
							const tagsA = verbatim.labels?.[typeA] || [];
							const tagsB = verbatim.labels?.[typeB] || [];
							return (
								<tr key={verbatim.id} ref={(el) => { rowRefs.current[verbatim.id] = el; }}>
									<td className="mlt-cell-id">{verbatim.id}</td>
									<td className="mlt-cell-verbatim">{renderVerbatimText(verbatim)}</td>
									<td>{renderTagList(tagsA)}</td>
									<td>{renderTagList(tagsB)}</td>
									{renderTagsCell(verbatim.id)}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);

	// --- Step 3 ---
	const renderStep3 = () => {
		const unresolvedIds = remainingIssueIds;

		return (
			<div className="mlt-step">
				<div className="mlt-summary-block">
					<h4>New type: {normalizedNewTypeName}</h4>
					<p>
						<strong>Options:</strong>
						<span>{OPTION_LABELS.mandatory}: {newTypeOptions.mandatory ? "yes" : "no"},</span>
						<span>{OPTION_LABELS.sentimentEnabled}: {newTypeOptions.sentimentEnabled ? "yes" : "no"},</span>
						<span>{OPTION_LABELS.multiValue}: {newTypeOptions.multiValue ? "yes" : "no"}</span>
					</p>
					<p><strong>Labels:</strong> {selectedLabelNames.join(", ")}</p>
				</div>

				{unresolvedIds.length > 0 && (
					<div className="mlt-alert-banner">
						{unresolvedIds.length} verbatim(s) still need a final tag: IDs {unresolvedIds.map((id) => `#${id}`).join(", ")}
					</div>
				)}

				<div className="mlt-table-wrapper">
					<table className="mlt-table">
						<thead>
							<tr>
								<th>ID</th>
								<th style={{ width: "30%" }}>Verbatim</th>
								<th style={{ width: "20%" }}>{typeA} (before)</th>
								<th style={{ width: "20%" }}>{typeB} (before)</th>
								<th style={{ width: "25%" }}>{normalizedNewTypeName} (after)</th>
							</tr>
						</thead>
						<tbody>
							{impactedVerbatims.map((verbatim) => {
								const tagsA = verbatim.labels?.[typeA] || [];
								const tagsB = verbatim.labels?.[typeB] || [];
								const newTags = taggingMap[verbatim.id];
								return (
									<tr key={verbatim.id}>
										<td className="mlt-cell-id">{verbatim.id}</td>
										<td className="mlt-cell-verbatim">{renderVerbatimText(verbatim)}</td>
										<td>{renderTagList(tagsA)}</td>
										<td>{renderTagList(tagsB)}</td>
										<td>{renderTagList(newTags)}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		);
	};

	// Stepper footer buttons
	const renderFooter = () => {
		if (step === 1) return (
			<div className="mlt-footer">
				<button className="mlt-btn-secondary" onClick={onClose}>Cancel</button>
				<button className="mlt-btn-primary" onClick={handleGoToStep2}>Next →</button>
			</div>
		);
		if (step === 2) return (
			<div className="mlt-footer">
				<button className="mlt-btn-secondary" onClick={() => setStep(1)}>← Back</button>
				<button
					className="mlt-btn-primary"
					onClick={handleGoToStep3}
					disabled={remainingIssueIds.length > 0}
					title={remainingIssueIds.length > 0 ? "Complete all impacted verbatims before continuing." : ""}
				>
					Next →
				</button>
			</div>
		);
		return (
			<div className="mlt-footer">
				<button className="mlt-btn-secondary" onClick={() => setStep(2)}>← Back</button>
				<button className="mlt-btn-danger" onClick={handleConfirmMerge} disabled={remainingIssueIds.length > 0}>Merge</button>
			</div>
		);
	};

	const stepLabels = ["1. Configuration", "2. Re-tagging", "3. Final review"];

	return (
		<div className="modal-backdrop">
			<div className="modal-content mlt-modal-content">
				<div className="modal-header">
					<h3>Merge Label Types</h3>
					<button className="close-button" onClick={onClose}>&times;</button>
				</div>

				<div className="mlt-global-warning">
					<i className="fa-solid fa-triangle-exclamation"></i>
					<span>
						The label type merge operation is irreversible. It is strongly recommended to save your project before proceeding to avoid losing data in case of error.
					</span>
				</div>

				{/* Stepper */}
				<div className="mlt-stepper">
					{stepLabels.map((label, i) => (
						<div key={i} className={`mlt-step-indicator${step === i + 1 ? " mlt-step-active" : step > i + 1 ? " mlt-step-done" : ""}`}>
							<span className="mlt-step-number">{i + 1}</span>
							<span className="mlt-step-label">{label}</span>
						</div>
					))}
				</div>

				<div className="modal-body">
					{step === 1 && renderStep1()}
					{step === 2 && renderStep2()}
					{step === 3 && renderStep3()}
				</div>

				{renderFooter()}
			</div>
		</div>
	);
};

export default MergeLabelTypesModal;
